import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Zap, TrendingUp, ArrowLeft, Activity, DollarSign,
  BarChart3, Calendar, ArrowUpRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientConsumption() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchConsumption();
  }, [period]);

  const fetchConsumption = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API_URL}/api/users/client/consumption?days=${period}`, { headers });
      setData(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  const stats = data?.stats || {};
  const daily = data?.daily_breakdown || [];
  const resold = data?.resold_energy || {};

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-green-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="back-dashboard">
            <ArrowLeft className="w-5 h-5 text-green-700" />
            <span className="font-semibold text-green-700">Dashboard</span>
          </Link>
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-green-600" />
            <span className="font-bold text-[#052e16]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Ma Consommation
            </span>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header with Period Select */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#052e16] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Ma Consommation
              </h1>
              <p className="text-[#64748b] mt-1">Suivi détaillé de votre énergie sur {period} jours</p>
            </div>
            <div className="flex gap-2" data-testid="period-selector">
              {[7, 30, 90].map((d) => (
                <Button
                  key={d}
                  variant={period === d ? 'default' : 'outline'}
                  onClick={() => setPeriod(d)}
                  className={`rounded-full px-5 ${
                    period === d
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'border-green-200 text-green-700 hover:bg-green-50'
                  }`}
                  data-testid={`period-${d}`}
                >
                  {d}j
                </Button>
              ))}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="consumption-stats">
            <Card className="bg-white border border-green-100 shadow-sm rounded-2xl hover:shadow-md transition-all">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mb-3">
                  <Zap className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="total-kwh">
                  {(stats.total_kwh || 0).toFixed(1)} kWh
                </p>
                <p className="text-sm text-[#64748b]">Énergie achetée</p>
              </CardContent>
            </Card>

            <Card className="bg-white border border-green-100 shadow-sm rounded-2xl hover:shadow-md transition-all">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center mb-3">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="total-spent">
                  {(stats.total_spent || 0).toFixed(2)} EUR
                </p>
                <p className="text-sm text-[#64748b]">Total dépensé</p>
              </CardContent>
            </Card>

            <Card className="bg-white border border-green-100 shadow-sm rounded-2xl hover:shadow-md transition-all">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-3">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
                <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="avg-price">
                  {(stats.avg_price || 0).toFixed(3)} EUR
                </p>
                <p className="text-sm text-[#64748b]">Prix moyen/kWh</p>
              </CardContent>
            </Card>

            <Card className="bg-white border border-green-100 shadow-sm rounded-2xl hover:shadow-md transition-all">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center mb-3">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="tx-count">
                  {stats.transaction_count || 0}
                </p>
                <p className="text-sm text-[#64748b]">Transactions</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* Daily Consumption Chart */}
            <Card className="bg-white border border-green-100 shadow-sm rounded-2xl" data-testid="daily-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Consommation journalière
                </CardTitle>
              </CardHeader>
              <CardContent>
                {daily.length > 0 ? (
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={daily}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#dcfce7" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 11, fill: '#64748b' }}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(v) => v.substring(5)}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: '#64748b' }}
                          tickLine={false}
                          axisLine={false}
                          unit=" kWh"
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            backdropFilter: 'blur(12px)',
                            border: '1px solid #dcfce7',
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          }}
                        />
                        <Bar dataKey="kwh" name="kWh" fill="#22c55e" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[280px] flex items-center justify-center text-[#64748b]">
                    Aucune donnée pour cette période
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Daily Cost Chart */}
            <Card className="bg-white border border-green-100 shadow-sm rounded-2xl" data-testid="cost-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Coût journalier
                </CardTitle>
              </CardHeader>
              <CardContent>
                {daily.length > 0 ? (
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={daily}>
                        <defs>
                          <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#16a34a" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#dcfce7" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 11, fill: '#64748b' }}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(v) => v.substring(5)}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: '#64748b' }}
                          tickLine={false}
                          axisLine={false}
                          unit=" EUR"
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            backdropFilter: 'blur(12px)',
                            border: '1px solid #dcfce7',
                            borderRadius: '12px',
                          }}
                        />
                        <Area type="monotone" dataKey="amount" name="EUR" stroke="#16a34a" strokeWidth={2} fill="url(#costGrad)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[280px] flex items-center justify-center text-[#64748b]">
                    Aucune donnée pour cette période
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Resold Energy Card */}
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 shadow-sm rounded-2xl" data-testid="resold-energy">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-600 flex items-center justify-center">
                    <ArrowUpRight className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#052e16]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Surplus revendu
                    </h3>
                    <p className="text-sm text-[#64748b]">Énergie que vous avez revendue au réseau</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-700" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="resold-kwh">
                    {(resold.total_kwh || 0).toFixed(1)} kWh
                  </p>
                  <p className="text-sm font-medium text-green-600" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="resold-earned">
                    +{(resold.total_earned || 0).toFixed(2)} EUR
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          {data?.transactions?.length > 0 && (
            <Card className="bg-white border border-green-100 shadow-sm rounded-2xl mt-6" data-testid="recent-transactions">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Dernières transactions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-green-100">
                        <th className="text-left py-3 px-2 text-xs uppercase tracking-widest font-semibold text-[#64748b]">Date</th>
                        <th className="text-right py-3 px-2 text-xs uppercase tracking-widest font-semibold text-[#64748b]">kWh</th>
                        <th className="text-right py-3 px-2 text-xs uppercase tracking-widest font-semibold text-[#64748b]">Prix/kWh</th>
                        <th className="text-right py-3 px-2 text-xs uppercase tracking-widest font-semibold text-[#64748b]">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.transactions.slice(0, 10).map((tx, i) => (
                        <tr key={i} className="border-b border-green-50 last:border-0 hover:bg-green-50/50">
                          <td className="py-3 px-2 text-[#052e16]">{tx.timestamp?.substring(0, 10)}</td>
                          <td className="py-3 px-2 text-right font-medium" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {tx.energy_kwh?.toFixed(2)}
                          </td>
                          <td className="py-3 px-2 text-right text-[#64748b]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {tx.price_per_kwh?.toFixed(3)}
                          </td>
                          <td className="py-3 px-2 text-right font-semibold text-green-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {tx.total_amount?.toFixed(2)} EUR
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
